<template>
  <!-- Phone-only bottom bar for admins (hidden from 768px up). The side menu stays as the full menu behind "More". -->
  <!-- tap outside the open drawer to close it -->
  <div v-if="stateStoreInstance.open" class="md:hidden mobile-backdrop" @click="stateStoreInstance.open = false"></div>
  <nav class="mobile-nav md:hidden" :class="{ dark: isDarkMode }">
    <router-link v-for="item in items" :key="item.to" :to="item.to" class="mobile-nav-item" :class="{ active: isActive(item.to) }" @click="closeMenu">
      <span class="icon-wrap">
        <v-icon :icon="item.icon" size="28"></v-icon>
        <span v-if="item.badge && unreadCount" class="badge-dot">{{ unreadCount > 99 ? '99+' : unreadCount }}</span>
      </span>
      <span class="label">{{ item.label }}</span>
    </router-link>
    <button class="mobile-nav-item" :class="{ active: stateStoreInstance.open }" @click="stateStoreInstance.onChange">
      <span class="icon-wrap"><v-icon :icon="stateStoreInstance.open ? 'mdi-close' : 'mdi-menu'" size="28"></v-icon></span>
      <span class="label">More</span>
    </button>
  </nav>
</template>

<script setup>
import { computed, watch } from 'vue';
import { useRoute } from 'vue-router';
import { storeToRefs } from 'pinia';
import stateStore from '@/utils/store';
import { useDarkModeStore } from '@/stores';
import { useChatStore } from '@/stores/chat.store';

const stateStoreInstance = stateStore;
const route = useRoute();
const chatStore = useChatStore();
const { isDarkMode } = storeToRefs(useDarkModeStore());
const unreadCount = computed(() => Number(chatStore.unreadCount || 0));

const items = [
  { to: '/admin/chat', icon: 'mdi-message-text', label: 'Messages', badge: true },
  { to: '/admin/license', icon: 'mdi-key-variant', label: 'Licences' },
  { to: '/admin/proxy', icon: 'mdi-server-network', label: 'Proxies' },
  { to: '/admin/server', icon: 'mdi-router-wireless', label: 'Servers' },
];
const isActive = (to) => route.path === to || route.path.startsWith(to + '/');
const closeMenu = () => { if (stateStoreInstance.open && window.innerWidth < 768) stateStoreInstance.open = false; };
// leaving a page from the full menu closes it on a phone
watch(() => route.path, closeMenu);
</script>

<style>
/* global (not scoped): room for the bar under every admin page on a phone */
@media only screen and (max-width: 767px) {
  .main-content { padding-bottom: 92px !important; font-size: 15px; }
  .sidebar-area.active { z-index: 1040; }
  .mobile-backdrop { position: fixed; inset: 0; z-index: 1036; background: rgba(0, 0, 0, 0.45); }
  /* drawer: bigger, easier to tap */
  .sidebar-area .sidebar-menu .sidebar-nav-link, .sidebar-area .sidebar-menu .accordion-button { font-size: 17px !important; padding-top: 12px !important; padding-bottom: 12px !important; }
  .sidebar-area .sidebar-menu .sidebar-nav-item { margin-bottom: 6px !important; }
  .header-burger-menu i { font-size: 30px !important; }
  /* readable, thumb-friendly controls on a phone */
  .main-content .v-btn { min-height: 40px; font-size: 14px; }
  .main-content .v-field input, .main-content .form-control, .main-content select { font-size: 16px; }
  .main-content .modal-body, .main-content .modal-title { font-size: 15px; }
  .main-content .btn-sm { padding: 6px 10px; font-size: 14px; }
}
</style>

<style scoped>
/* desktop: never shown (scoped rules outrank the md:hidden utility) */
@media only screen and (min-width: 768px) { .mobile-nav, .mobile-backdrop { display: none !important; } }
<style scoped>
.mobile-nav {
  position: fixed; left: 0; right: 0; bottom: 0; z-index: 1050;
  display: flex; justify-content: space-around; align-items: stretch;
  height: 72px; padding-bottom: env(safe-area-inset-bottom);
  background: #ffffff; border-top: 1px solid #e5e7eb;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.06);
}
.mobile-nav.dark { background: #2b2a3f; border-top-color: #3d3c55; }
.mobile-nav-item {
  flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 2px;
  color: #6b7280; text-decoration: none; background: transparent; border: 0; font-size: 13px; font-weight: 600; line-height: 1;
}
.mobile-nav.dark .mobile-nav-item { color: #a1a1b5; }
.mobile-nav-item.active { color: #4f46e5; }
.mobile-nav.dark .mobile-nav-item.active { color: #a5b4fc; }
.icon-wrap { position: relative; display: inline-flex; }
.badge-dot {
  position: absolute; top: -6px; right: -12px; min-width: 18px; height: 18px; padding: 0 5px;
  border-radius: 9px; background: #dc2626; color: #fff; font-size: 10px; font-weight: 700;
  display: flex; align-items: center; justify-content: center;
}
</style>
